import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import IndividualisedRehabPlanForm from "./IndividualisedRehabPlanForm";
import {
  createRehabPlan,
  clearError,
} from "../../../store/slices/individualisedRehabPlanSlice";

const AddIndividualisedRehabPlan = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { isLoading, error } = useSelector(
    (state) => state.individualisedRehabPlan
  );

  // --------------------------------------------------------------
  // 1. Form state (only fields that are sent to the API)
  // --------------------------------------------------------------
  const [formData, setFormData] = useState({
    patientId, // initialize
    caseId,    // initialize
    domain: "",
    strengths: [{ value: "" }],
    weaknesses: [{ value: "" }],
    objectives: [
      {
        name: "",
        obj_status: "Pending",
        obj_date: new Date().toISOString().split("T")[0],
        actions: [
          {
            action_name: "",
            location: "",
            duration: "",
            frequency: "",
            // target_date will be added later if needed
          },
        ],
      },
    ],
  });

  // --------------------------------------------------------------
  // 2. Validation (client‑side only)
  // --------------------------------------------------------------
  const validate = () => {
    const errs = {};

    if (!formData.domain) errs.domain = "Please select a domain.";
    if (!formData.strengths.some((s) => s.value.trim()))
      errs.strengths = "Add at least one strength.";
    if (!formData.weaknesses.some((w) => w.value.trim()))
      errs.weaknesses = "Add at least one weakness.";

    formData.objectives.forEach((obj, i) => {
      if (!obj.name.trim()) errs[`obj_name_${i}`] = "Objective name required.";
      if (!obj.obj_status) errs[`obj_status_${i}`] = "Select a status.";
      if (!obj.obj_date) errs[`obj_date_${i}`] = "Date is required.";

      if (!obj.actions || obj.actions.length === 0) {
        errs[`obj_actions_${i}`] =
          `Objective ${i + 1} must have at least one action.`;
      } else {
        obj.actions.forEach((act, j) => {
          if (!act.action_name.trim())
            errs[`act_name_${i}_${j}`] =
              `Objective ${i + 1} - Action ${j + 1} name required.`;
        });
      }
    });

    return errs;
  };

  // --------------------------------------------------------------
  // 3. Submit → thunk
  // --------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      Swal.fire({
        title: "Missing fields",
        html: Object.values(validationErrors).join("<br/>"),
        icon: "warning",
      });
      return;
    }

    const payload = {
      patient_id: formData.patientId || null,
      case_id: formData.caseId || null,
      domain: formData.domain,
      strengths: formData.strengths.map((s) => ({ value: s.value.trim() })),
      weaknesses: formData.weaknesses.map((w) => ({ value: w.value.trim() })),
      objectives: formData.objectives.map((obj) => ({
        name: obj.name.trim(),
        obj_status: obj.obj_status,
        obj_date: obj.obj_date,
        actions: obj.actions.map((a) => ({
          action_name: a.action_name.trim(),
          location: a.location?.trim() || null,
          duration: a.duration?.trim() || null,
          frequency: a.frequency?.trim() || null,
        })),
      })),
    };

    const result = await dispatch(createRehabPlan(payload));

    if (createRehabPlan.fulfilled.match(result)) {
      Swal.fire({
        title: "Success!",
        text: "Rehab plan created successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
      const res = result.payload || result;
            navigate(`/phase05/individualised-rehab-plans/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
    } else {
      // error already in Redux slice
      Swal.fire({
        title: "Error",
        text: result.payload || "Failed to create plan.",
        icon: "error",
      });
    }
  };

  // --------------------------------------------------------------
  // 4. Render
  // --------------------------------------------------------------
  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="card shadow-sm border-0">
          <div className="card-body">
            {/* Redux error */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                <strong>Error:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => dispatch(clearError())}
                />
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <IndividualisedRehabPlanForm
                formData={formData}
                setFormData={setFormData}
              />
              <div className="col-12 d-flex justify-content-end mt-4">
                <div className="col-md-2 d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <i className="ri-loader-4-line me-1 animate-spin"></i> Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-2-line me-1"></i> Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddIndividualisedRehabPlan;